import React from 'react';
import { Edit2, Trash2, UserX, UserCheck, Key, UserCog, Link, Unlink } from 'lucide-react';
import { getRoleLabel, getRoleColor } from '../utils/userValidation';

const ROLE_COLOR = {
  super_admin:   'bg-red-50 text-red-600',
  company_admin: 'bg-purple-50 text-purple-600',
  supervisor:    'bg-orange-50 text-orange-600',
  rrhh:          'bg-blue-50 text-blue-600',
  employee:      'bg-green-50 text-green-600',
};

export default function UserTable({ users, onEdit, onToggleStatus, onResetPassword, onDelete, onUnlink }) {
  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <UserCog className="w-10 h-10 text-gray-200 mb-3" />
        <p className="text-sm text-gray-400">No hay usuarios registrados</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Usuario</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Nombre</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Email</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden xl:table-cell">Empleado</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Rol</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Estado</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {users.map(user => (
            <tr
              key={user.id}
              onClick={() => onEdit(user)}
              className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
            >
              {/* Usuario */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-gray-500">
                      {user.username?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">{user.username}</span>
                </div>
              </td>

              {/* Nombre */}
              <td className="px-4 py-3 text-gray-500 hidden md:table-cell">
                {user.nombre || user.apellido
                  ? `${user.nombre || ''} ${user.apellido || ''}`.trim()
                  : <span className="text-gray-300">—</span>
                }
              </td>

              {/* Email */}
              <td className="px-4 py-3 text-gray-500 hidden lg:table-cell truncate max-w-[180px]">
                {user.email || <span className="text-gray-300">—</span>}
              </td>

              {/* Empleado vinculado */}
              <td className="px-4 py-3 hidden xl:table-cell">
                {user.employeeNombre ? (
                  <div className="flex items-center gap-1.5">
                    <Link size={12} className="text-indigo-400 shrink-0" />
                    <div>
                      <span className="text-sm text-gray-700">{user.employeeNombre}</span>
                      <span className="text-xs text-gray-400 ml-1 font-mono">{user.employeeCode}</span>
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-300 italic">Sin empleado</span>
                )}
              </td>

              {/* Rol */}
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLOR[user.rol] || 'bg-gray-50 text-gray-600'}`}>
                  {getRoleLabel(user.rol)}
                </span>
              </td>

              {/* Estado */}
              <td className="px-4 py-3 hidden sm:table-cell">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.companyActivo ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                }`}>
                  {user.companyActivo ? 'Activo' : 'Inactivo'}
                </span>
              </td>

              {/* Acciones */}
              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onEdit(user)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    onClick={() => onToggleStatus(user.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      user.companyActivo
                        ? 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={user.companyActivo ? 'Desactivar' : 'Activar'}
                  >
                    {user.companyActivo ? <UserX size={15} /> : <UserCheck size={15} />}
                  </button>
                  <button
                    onClick={() => onResetPassword(user.id)}
                    className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Cambiar contraseña"
                  >
                    <Key size={15} />
                  </button>
                  {user.employee_id && onUnlink && (
                    <button
                      onClick={() => onUnlink(user)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Desvincular empleado"
                    >
                      <Unlink size={15} />
                    </button>
                  )}
                  {user.username !== 'admin' && (
                    <button
                      onClick={() => onDelete(user.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
